/**
 * "Why isn't it converting?" reports — recharge / discovery / trials. One panel
 * renders any of them off the shared backend contract:
 *   { title, eligible, eligible_label, succeeded, succeeded_label, not_done,
 *     buckets:[{key,label,count,rows}], report_types:[{key,label}] }
 * Per centre + month; read-only; CSV export for sharing.
 */
import React, { useCallback, useEffect, useState } from 'react'

import { fetchConversionReport } from 'api'
import { showToast } from 'app/helpers'

import { dayMonthYear, monthLabel } from './monthHelpers'

const DEFAULT_TYPES = [
  { key: 'recharge', label: 'Recharge', programme: 'afterschool' },
  { key: 'discovery', label: 'Discovery conversion', programme: 'afterschool' },
  { key: 'lost_parents', label: 'Lost parents', programme: 'afterschool' },
  { key: 'lost_enrolments', label: 'Lost enrolments', programme: 'afterschool' },
  { key: 'downsell', label: 'Downsell', programme: 'afterschool' },
  { key: 'upsell', label: 'Upsell', programme: 'afterschool' },
  { key: 'paused', label: 'Paused', programme: 'afterschool' },
  { key: 'tc_renewal', label: 'Toddler Club renewal', programme: 'toddler_club' },
  { key: 'trials', label: 'New Visitors', programme: 'all' }
]

// Report chips are grouped under their programme (user 2026-08-03); New Visitors
// is the one cross-programme report, at the bottom under "All programmes".
const PROGRAMME_GROUPS = [
  ['afterschool', 'Afterschool'],
  ['toddler_club', 'Toddler Club'],
  ['all', 'All programmes']
]

// Colour by meaning — green = committed, amber = persuadable, grey = lost,
// red = the actionable gap (never contacted / unknown intent).
const CAT_COLOR = (key) =>
  ({
    will_recharge: '#2e7d32',
    ready: '#2e7d32',
    promised: '#2e7d32',
    // The "done" wins — green (recharge's Recharged, discovery's Converted,
    // TC's Renewed).
    done: '#2e7d32',
    converted: '#2e7d32',
    renewed: '#2e7d32',
    // Upsell (user 2026-09-24): a course added = the win; asked-about-more =
    // the persuadable worklist (amber default). Paused keeps its lane colour.
    upsold: '#2e7d32',
    paused: '#B26A00',
    will_not: '#6a6a6a',
    churning: '#6a6a6a',
    declined: '#6a6a6a',
    undecided: '#b06800',
    unknown: '#C62828',
    not_contacted: '#C62828',
    never_contacted: '#C62828',
    // Helios recharge status buckets (2026-08-02): recharged = green (the win),
    // the actionable pending group red, a late payment amber, a non-payment
    // drop-off grey.
    done: '#2e7d32',
    still_needs: '#C62828',
    late: '#b06800',
    other: '#6a6a6a'
  }[key] || '#b06800')

const fmt = (iso) => (iso ? dayMonthYear(iso) : '')
// On screen the note is a glance; the CSV carries it in full.
const clip = (text, n = 240) => (text && text.length > n ? `${text.slice(0, n)}…` : text)

const rowMeta = (r) => {
  const bits = []
  // Downsell: current → next enrolment count, and the subjects dropped.
  if (r.current_count != null && r.next_count != null) {
    bits.push(`${r.current_count} → ${r.next_count} enrolments`)
  }
  // Upsell: last month → this month enrolment count, and the subjects added.
  if (r.prev_count != null && r.current_count != null && r.next_count == null) {
    bits.push(`${r.prev_count} → ${r.current_count} enrolments`)
  }
  if (r.dropped) bits.push(`dropped ${r.dropped}`)
  if (r.added) bits.push(`added ${r.added}`)
  if (r.coins_held != null) bits.push(`${r.coins_held} coins`)
  if (r.subject) bits.push(r.subject)
  if (r.visit_date) bits.push(`visited ${fmt(r.visit_date)}`)
  if (r.paused_on) bits.push(`paused ${fmt(r.paused_on)}`)
  if (r.end_date) bits.push(`ended ${fmt(r.end_date)}`)
  if (r.promised_date) {
    bits.push(`promised ${fmt(r.promised_date)}${r.promise_overdue ? ' · overdue' : ''}`)
  }
  if (r.last_conversation) {
    bits.push(`last spoke ${fmt(r.last_conversation)}${r.stale ? ' ⚠ before this month' : ''}`)
  } else if (r.last_contact) {
    // Contacted about something else (e.g. an enquiry) — not about this topic.
    bits.push(`last contacted ${fmt(r.last_contact)}`)
  } else bits.push('never contacted')
  return bits.join(' · ')
}

// The last_interaction_* columns (user 2026-09-24) carry the parent's latest
// interaction of any topic — date, topic, outcome, who logged it and the full
// note — so a centre RCA can be done from the export alone. Stamped on every
// report's rows server-side (reports.py _stamp_last_interaction).
const toCsv = (data) => {
  const head = ['category', 'pattern', 'parent', 'phone', 'coins', 'subject', 'previous_enrolments', 'current_enrolments', 'next_enrolments', 'dropped', 'added', 'visit_date', 'paused_on', 'end_date', 'promised_date', 'last_conversation', 'note', 'last_interaction_date', 'last_interaction_topic', 'last_interaction_outcome', 'last_interaction_by', 'last_interaction_note']
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
  const lines = [head.join(',')]
  const emit = (cat, pat, r) => lines.push([
    cat, pat, r.parent_name, r.phone, r.coins_held, r.subject,
    r.prev_count, r.current_count, r.next_count, r.dropped, r.added,
    r.visit_date, r.paused_on, r.end_date, r.promised_date, r.last_conversation, r.last_note,
    r.last_interaction_date, r.last_interaction_topic, r.last_interaction_outcome,
    r.last_interaction_by, r.last_interaction_note
  ].map(esc).join(','))
  ;(data.buckets || []).forEach((b) => {
    if (b.subgroups && b.subgroups.length) {
      b.subgroups.forEach((s) => (s.rows || []).forEach((r) => emit(b.label, s.label, r)))
    } else {
      (b.rows || []).forEach((r) => emit(b.label, '', r))
    }
  })
  return lines.join('\n')
}

const ReportsPanel = ({ centreUuid, month }) => {
  const [type, setType] = useState('recharge')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(null)
  const [openSub, setOpenSub] = useState(null)
  const [loadedAt, setLoadedAt] = useState(null)
  // Autopay filter — narrows the rows in every bucket: 'all' | 'yes' | 'no'.
  const [apFilter, setApFilter] = useState('all')
  // Subject filter for the Lost enrolments report (its buckets ARE subjects):
  // 'all' or a subject key.
  const [subjFilter, setSubjFilter] = useState('all')

  const load = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      setData(await fetchConversionReport(centreUuid, month, type))
      setLoadedAt(new Date())
    } catch (e) {
      showToast('Could not load the report', 'error')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [centreUuid, month, type])

  useEffect(() => { load() }, [load])

  const types = data?.report_types || DEFAULT_TYPES
  const downloadCsv = () => {
    if (!data) return
    const blob = new Blob([toCsv(data)], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${type}-report-${month}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const renderRows = (rowsIn) => {
   const rows = apFilter === 'yes'
     ? rowsIn.filter((r) => r.autopay)
     : apFilter === 'no'
       ? rowsIn.filter((r) => !r.autopay)
       : rowsIn
   return (
    <div>
      {rows.map((r) => (
        <div key={r.parent_user_uuid + (r.subject || '')} style={{ padding: '9px 14px 9px 22px', borderBottom: '1px solid #f4f6f9' }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>
            <a href={`/home/parent?parent=${encodeURIComponent(r.parent_user_uuid)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'none' }}>
              {r.parent_name}
            </a>
            {r.phone ? <span style={{ color: '#9aa1ad', fontWeight: 400, marginLeft: 8 }}>{r.phone}</span> : null}
            {/* Autopay = confirmed e-mandate (user 2026-08-02): the recharge is
                on autopilot, so this parent is a low-priority chase. */}
            {r.autopay ? (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#2e7d32', background: '#e8f5e9', borderRadius: 6, padding: '1px 7px' }}>
                Autopay
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 12, color: '#69707d', marginTop: 2 }}>{rowMeta(r)}</div>
          {/* The last call on this report's own topic (user 2026-09-24) —
              the same interaction the CSV's last_interaction_* columns carry. */}
          {data.interaction_topic ? (
            <div style={{ fontSize: 12, color: r.last_interaction_date ? '#48505c' : '#C62828', marginTop: 2 }}>
              {r.last_interaction_date
                ? [
                    `Last ${data.interaction_topic} call ${fmt(r.last_interaction_date)}`,
                    r.last_interaction_outcome,
                    r.last_interaction_by
                  ].filter(Boolean).join(' · ')
                : `No ${data.interaction_topic} call logged`}
            </div>
          ) : null}
          {(r.last_interaction_note || r.last_note) ? (
            <div style={{ fontSize: 12.5, color: '#48505c', fontStyle: 'italic', marginTop: 3 }}>
              “{clip(r.last_interaction_note || r.last_note)}”
            </div>
          ) : null}
        </div>
      ))}
    </div>
   )
  }

  return (
    <div style={{ border: '1px solid #e6e9ef', borderRadius: 12, padding: 16, background: '#fff' }}>
      {/* Controls — month + refresh + export, on their own row. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9aa1ad' }}>
          {monthLabel(month)}
          {loadedAt ? ` · as of ${loadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </span>
        <span
          onClick={() => { if (!loading) load() }}
          style={{ cursor: loading ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, color: loading ? '#9aa1ad' : '#1565C0', border: '1px solid #cfe0f5', borderRadius: 8, padding: '5px 10px' }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </span>
        <span
          onClick={downloadCsv}
          style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1565C0', border: '1px solid #cfe0f5', borderRadius: 8, padding: '5px 10px' }}
        >
          Export CSV
        </span>
      </div>

      {/* Report chips grouped by programme (user 2026-08-03). */}
      {PROGRAMME_GROUPS.map(([prog, plabel]) => {
        const group = types.filter((t) => (t.programme || 'all') === prog)
        if (!group.length) return null
        return (
          <div key={prog} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 10.5, color: '#9aa1ad', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, width: 96, flexShrink: 0 }}>{plabel}</span>
            {group.map((t) => (
              <span
                key={t.key}
                onClick={() => { setType(t.key); setOpen(null); setApFilter('all'); setSubjFilter('all') }}
                style={{
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '6px 13px',
                  borderRadius: 20, border: '1px solid',
                  borderColor: type === t.key ? '#1565C0' : '#e6e9ef',
                  background: type === t.key ? '#1565C0' : '#fff',
                  color: type === t.key ? '#fff' : '#69707d'
                }}
              >
                {t.label}
              </span>
            ))}
          </div>
        )
      })}

      {loading && <div style={{ color: '#69707d', fontSize: 13, padding: 8 }}>Loading…</div>}

      {!loading && data && (
        <>
          <div style={{ fontSize: 15, fontWeight: 680, marginBottom: 2 }}>{data.title}</div>
          <div style={{ fontSize: 13, color: '#69707d', marginBottom: 14 }}>
            <b style={{ color: '#1b1f27' }}>{data.eligible}</b> {data.eligible_label}
            {data.succeeded != null && (
              <> · <b style={{ color: '#2e7d32' }}>{data.succeeded}</b> {data.succeeded_label}</>
            )}
            {/* On autopay — a confirmed mandate will auto-collect, so it sits
                next to "recharged in time" and is NOT in the pending total. */}
            {data.on_autopay != null && data.on_autopay > 0 && (
              <> · <b style={{ color: '#1565C0' }}>{data.on_autopay}</b> {data.on_autopay_label || 'on autopay'}</>
            )}
            {/* Late recharge sits next to "recharged in time" — it's a recharge
                too, just after month-end (user 2026-08-02), and is NOT in the
                pending total below. */}
            {(() => {
              const late = (data.buckets || []).find((b) => b.key === 'late')
              return late != null
                ? <> · <b style={{ color: '#b06800' }}>{late.count}</b> late recharge</>
                : null
            })()}
            {' · '}<b style={{ color: '#C62828' }}>{data.not_done}</b> {data.not_done_label || 'pending'}
          </div>

          {/* Autopay filter — narrows every bucket's rows; shown only when the
              report has autopay parents (user 2026-08-02). */}
          {(() => {
            const rows = (data.buckets || []).flatMap((b) => b.rows || [])
            const n = rows.filter((r) => r.autopay).length
            if (!n) return null
            const chips = [['all', 'All', rows.length], ['yes', 'Autopay', n], ['no', 'No autopay', rows.length - n]]
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: '#9aa1ad' }}>Autopay</span>
                {chips.map(([k, label, c]) => (
                  <span
                    key={k}
                    onClick={() => setApFilter(k)}
                    style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 16, border: '1px solid', borderColor: apFilter === k ? '#1565C0' : '#e6e9ef', background: apFilter === k ? '#1565C0' : '#fff', color: apFilter === k ? '#fff' : '#69707d' }}
                  >
                    {label} <span style={{ opacity: 0.7 }}>{c}</span>
                  </span>
                ))}
              </div>
            )
          })()}

          {/* Subject chips — Lost enrolments is bucketed by subject; the chips
              narrow to one subject (user 2026-08-02). */}
          {data.report === 'lost_enrolments' && (data.buckets || []).length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#9aa1ad' }}>Subject</span>
              {[['all', 'All', (data.buckets || []).reduce((s, b) => s + b.count, 0)],
                ...(data.buckets || []).map((b) => [b.key, b.label, b.count])].map(([k, label, c]) => (
                <span
                  key={k}
                  onClick={() => setSubjFilter(k)}
                  style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 16, border: '1px solid', borderColor: subjFilter === k ? '#1565C0' : '#e6e9ef', background: subjFilter === k ? '#1565C0' : '#fff', color: subjFilter === k ? '#fff' : '#69707d' }}
                >
                  {label} <span style={{ opacity: 0.7 }}>{c}</span>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Recharge shows ALL its outcome buckets (Recharged / Still needs /
                Late / Other) even at 0, so Late & Other are always accounted for
                (user 2026-08-02); other reports hide empty buckets. */}
            {(data.buckets || [])
              .filter((b) => b.count > 0 || data.report === 'recharge')
              .filter((b) => subjFilter === 'all' || b.key === subjFilter)
              .map((b) => (
              <div key={b.key} style={{ border: '1px solid #eef1f6', borderRadius: 10, overflow: 'hidden' }}>
                <div
                  onClick={() => { setOpen(open === b.key ? null : b.key); setOpenSub(null) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: CAT_COLOR(b.key) }} />
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{b.label}</span>
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: CAT_COLOR(b.key) }}>{b.count}</span>
                  <span style={{ color: '#9aa1ad', fontSize: 12 }}>{open === b.key ? '▲' : '▼'}</span>
                </div>
                {open === b.key && (
                  <div style={{ borderTop: '1px solid #eef1f6' }}>
                    {/* Level 2: patterns (Will-not / Undecided / Unknown), else the rows. */}
                    {(b.subgroups && b.subgroups.length)
                      ? b.subgroups.map((s) => {
                        const sid = `${b.key}:${s.key}`
                        return (
                          <div key={sid}>
                            <div
                              onClick={() => setOpenSub(openSub === sid ? null : sid)}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px 8px 22px', cursor: 'pointer', background: '#fafbfc', borderBottom: '1px solid #f0f2f6' }}
                            >
                              <span style={{ fontSize: 12.5, color: '#48505c', fontWeight: 500 }}>{s.label}</span>
                              <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 12.5, color: '#69707d' }}>{s.count}</span>
                              <span style={{ color: '#c3cad4', fontSize: 11 }}>{openSub === sid ? '▲' : '▼'}</span>
                            </div>
                            {openSub === sid && renderRows(s.rows)}
                          </div>
                        )
                      })
                      : renderRows(b.rows)}
                  </div>
                )}
              </div>
            ))}
            {data.not_done === 0 && (() => {
              // Report-aware empty state — "nobody was eligible" reads very
              // differently from "everyone converted" (user 2026-07-31).
              let msg
              let good = true
              if (data.report === 'trials') {
                if (data.had_visitors) msg = 'All new visitors this month have already enrolled. 🎉'
                else { msg = 'No new visitors attended this month.'; good = false }
              } else if (data.report === 'discovery') {
                msg = data.eligible > 0
                  ? 'Every discovery that ended this month converted to a plan. 🎉'
                  : (good = false, 'No discovery periods ended this month.')
              } else if (data.report === 'lost_parents') {
                msg = data.eligible > 0
                  ? 'Every active parent this month has a future booking. 🎉'
                  : (good = false, 'No active afterschool parents this month.')
              } else if (data.report === 'lost_enrolments') {
                msg = data.eligible > 0
                  ? 'Every active enrolment this month continues. 🎉'
                  : (good = false, 'No active afterschool enrolments this month.')
              } else if (data.report === 'upsell') {
                good = false
                msg = data.eligible > 0
                  ? 'No one else has asked about more this month.'
                  : 'No active afterschool parents this month.'
              } else if (data.report === 'paused') {
                good = false
                msg = 'No afterschool enrolments are paused this month.'
              } else if (data.report === 'tc_renewal') {
                msg = data.eligible > 0
                  ? 'Every Toddler Club subscription ending this month renewed. 🎉'
                  : (good = false, 'No Toddler Club subscriptions ending this month.')
              } else {
                msg = data.eligible > 0
                  ? 'Everyone who needed to recharge has already done it. 🎉'
                  : (good = false, 'No one is due to recharge this month.')
              }
              return (
                <div style={{ color: good ? '#2e7d32' : '#69707d', fontSize: 13, padding: 10, textAlign: 'center' }}>
                  {msg}
                </div>
              )
            })()}
          </div>
        </>
      )}
    </div>
  )
}

export default ReportsPanel
